<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.fouls', 2),
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <BadgePill>
                            <a :href="meta.links.foul_stats">{{ $t('messages.graphs') }}</a>
                        </BadgePill>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-4">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.search')" for="search" />
                        <FormInput
                            id="search"
                            v-model.trim="form.search"
                            name="search"
                            type="text"
                            max="180"
                            :placeholder="`${$t('messages.search')}: ${$t('messages.public_description')}, ${$t('messages.punishment')}, ${$t('messages.internal_teacher_note')}`"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.search" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.groups', 1)" for="group_id" />
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.subject', 1)" for="subject_id" />
                        <FormSelect
                            id="subject_id"
                            v-model="form.subject_id"
                            :options="options.subjects"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2" v-show="options.school_years.length > 1">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                        <FormSelect
                            id="school_year_id"
                            v-model="form.school_year_id"
                            :options="options.school_years"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.type')" for="type" />
                        <FormSelect id="type" v-model="form.type" :options="options.types" class="w-full mt-1" />
                        <FormError :value="form.errors.type" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.severity')" for="severity" />
                        <FormSelect
                            id="severity"
                            v-model="form.severity"
                            :options="options.severities"
                            class="w-full mt-1"
                        />
                        <FormError :value="form.errors.severity" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_from')" for="date_from" />
                        <FormInput id="date_from" v-model="form.date_from" type="date" name="date_from" class="mt-1" />
                        <FormError :value="form.errors.date_from" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_to')" for="date_to" />
                        <FormInput id="date_to" v-model="form.date_to" type="date" name="date_to" class="mt-1" />
                        <FormError :value="form.errors.date_to" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="fouls.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div v-else class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.students', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.subject', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.type') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(foul, index) in fouls.data"
                                :key="foul.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div>
                                        <InertiaLink :href="foul.student.links.show" class="text-indigo-500">
                                            {{ foul.student.full_name }}
                                        </InertiaLink>
                                    </div>
                                    <div class="text-gray-500 text-xs">{{ foul.teacher_name }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ foul.subject_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div>{{ foul.date }}</div>
                                    <div class="text-sm">{{ foul.turn_display }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div>
                                        {{ foul.type_display }}
                                    </div>
                                    <div>
                                        {{ foul.severity_display }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton element="a" action="show" :href="foul.links.show" />
                                    <InertiaSquareLinkButton
                                        element="a"
                                        action="print"
                                        :href="foul.links.print"
                                        :title="$t('messages.print')"
                                        target="_blank"
                                    />
                                    <InertiaSquareLinkButton
                                        element="a"
                                        action="edit"
                                        :href="foul.links.edit"
                                        v-if="foul.can.edit"
                                    />
                                    <InertiaSquareDeleteButton
                                        :href="foul.links.delete"
                                        v-if="foul.can.delete"
                                        :confirm-text="$t('messages.are_you_sure?')"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="fouls" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import EmptyState from '@/components/EmptyState.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import BadgePill from '@/components/BadgePill.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormInput from '@/components/Forms/FormInput.vue';

export default {
    components: {
        FormInput,
        SubmitFormButton,
        InertiaPagination,
        BadgePill,
        InertiaSquareDeleteButton,
        PanelPage,
        InertiaSquareLinkButton,
        FormLabel,
        FormError,
        FormSelect,
        EmptyState,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'fouls', 'filters', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
                subject_id: this.filters.subject_id,
                school_year_id: this.filters.school_year_id,
                group_id: this.filters.group_id,
                type: this.filters.type,
                severity: this.filters.severity,
                date_from: this.filters.date_from,
                date_to: this.filters.date_to,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
